<template>
  <v-dialog
    v-model="isVisible"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    scrollable
    persistent
    scroll-strategy="block"
    retain-focus
    @click:outside="refocusPreviewDialog"
  >
    <v-card class="overflow-y-auto">
      <main class="min-h-screen bg-white">
        
        <!-- Background elements -->
        <UnderwaterBg class="fixed inset-0 z-0" />

        <!-- Preview Header with Back Button -->
        <div class="max-w-7xl mx-auto px-4 mb-6 relative z-10">
          <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <v-btn
              color="primary"
              variant="elevated"
              prepend-icon="mdi-arrow-left"
              ref="mobileReturnBtnRef"
              @click="closePreview"
              class="mt-4 shadow-lg bg-blue-600 text-white hover:bg-blue-700"
              size="large"
            >
              Back to Admin
            </v-btn>
            <v-chip 
              color="warning" 
              variant="elevated" 
              size="large" 
              class="mt-4 shadow-lg bg-amber-500 text-white font-bold px-4 py-2"
            >
              <v-icon start size="small">mdi-eye-outline</v-icon>
              PREVIEW MODE
            </v-chip>
          </div>
        </div>

        <!-- Header with Logo - Improved mobile padding -->
        <div class="relative z-10 flex justify-center py-4 md:py-8 animate-fade-in px-4">
          <div class="group flex items-center gap-3 transition-transform hover:scale-105">
            <div class="relative">
              <img src="/ducksImages/duckIcon.webp" class="w-12 h-12 drop-shadow-xl group-hover:animate-wiggle" alt="DuckNSeek Logo" />
              <div class="absolute -bottom-1 -right-1 w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center transform group-hover:scale-125 transition-transform">
                <span class="text-xs font-bold text-white">!</span>
              </div>
            </div>
            <div class="flex flex-col">
              <div class="flex items-center">
                <h1 class="font-bold text-xl md:text-2xl text-slate-800">DuckNSeek</h1>
                <span class="ml-1 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full inline-flex items-center">
                  <v-icon size="12" class="mr-0.5">mdi-information</v-icon>Learn More
                </span>
              </div>
              <span class="text-xs text-gray-600 group-hover:text-yellow-600 transition-colors">Click for project info</span>
            </div>
          </div>
        </div>

        <!-- Main Content - Better padding on small screens -->
        <div class="relative z-10 px-3 sm:px-6 md:px-10 max-w-4xl mx-auto py-3 sm:py-6 flex-grow">
            
            <!-- Duck Discovery Section -->
            <DiscoverySection :duck="previewDuck" />
                    
            <!-- About Us Section -->
            <AboutSection
              :cruise="cruise"
              class="mb-12"
            />
            
            <!-- Message Section - Grayed Out Placeholder -->
            <div class="mb-12 opacity-50 pointer-events-none">
              <div class="bg-gradient-to-br from-white/90 via-white/85 to-white/80 backdrop-blur-sm rounded-xl p-6 md:p-8 shadow-lg border border-gray-100">
                <div class="max-w-2xl mx-auto px-4">
                  <div class="flex items-center mb-6">
                    <div class="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center mr-4 flex-shrink-0 shadow-md">
                      <v-icon color="white" size="24">mdi-message-outline</v-icon>
                    </div>
                    <div>
                      <h2 class="text-2xl font-bold text-slate-800">Send a Message</h2>
                      <div class="h-1 w-24 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full mt-1"></div>
                    </div>
                  </div>
                  
                  <p class="text-gray-600 mb-6 leading-relaxed">
                    Share your discovery with the duck owner! This is where duck hunters can send messages about finding your ducks.
                  </p>
                  
                  <div class="bg-gray-100 rounded-lg p-4 mb-4">
                    <v-textarea
                      placeholder="Write your message here..."
                      variant="outlined"
                      rows="3"
                      disabled
                      class="mb-4"
                    />
                    <v-btn
                      color="primary"
                      prepend-icon="mdi-send"
                      disabled
                    >
                      Send Message
                    </v-btn>
                  </div>
                  
                  <div class="mt-6 pt-6 border-t border-gray-200">
                    <h3 class="text-lg font-semibold text-gray-700 mb-4">Message History</h3>
                    <div class="space-y-3">
                      <div class="bg-gray-50 rounded-lg p-3">
                        <p class="text-sm text-gray-600 italic">Messages from duck hunters will appear here...</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Social Links Section -->
            <SocialLinksSection 
              :cruise="cruise" 
              @share="() => {}" 
              class="mb-8 sm:mb-12" 
            />

            <!-- Ducks List -->
            <DucksListSection class="mb-12" />

            <!-- 3D Text -->
            <Motion
              :initial="{ opacity: 0, scale: 0.9 }"
              :animate="{ opacity: 1, scale: 1 }"
              :transition="{ duration: 0.7, delay: 0.6 }"
              class="text-center py-8"
            >
              <Text3d class="text-5xl sm:text-6xl md:text-8xl font-bold">
                Happy Duck Hunting!
              </Text3d>
            </Motion>
          </div>

        </main>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, provide, defineAsyncComponent, watch, onMounted, onUnmounted, ref, nextTick } from 'vue';
import type { DetailedCruise } from '@/interfaces/cruise';
import { useDuckStore } from '@/stores/duckStore';
import { useCruiseStore } from '@/stores/cruiseStore';

// UI components
import { UnderwaterBg } from '@/components/ui/underwaterBg';
import { Motion } from 'motion-v';

// DuckView components
import DiscoverySection from '@/components/duckview/DiscoverySection.vue';
import AboutSection from '@/components/duckview/AboutSection.vue';
import SocialLinksSection from '@/components/duckview/SocialLinksSection.vue';

// Async components
const DucksListSection = defineAsyncComponent(() => import("@/components/duckview/DucksListSection.vue"));
const Text3d = defineAsyncComponent(() => import('@/components/ui/text3d').then(m => m.Text3d));

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  cruise: {
    type: Object as () => DetailedCruise,
    required: true
  }
});

const emit = defineEmits(['update:modelValue']);

const duckStore = useDuckStore();
const cruiseStore = useCruiseStore();

// Store original state to restore later
let originalDucks: any[] = [];
let originalCruise: any = null;

const isVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

// Focus targets and refocus handler
const mobileReturnBtnRef = ref<HTMLElement | null>(null);
const refocusPreviewDialog = () => {
  // Focus on the mobile return button
  if (mobileReturnBtnRef.value) {
    mobileReturnBtnRef.value.focus?.();
  }
};
watch(isVisible, async (open) => {
  if (open) {
    await nextTick();
    refocusPreviewDialog();
  }
});

// Create a preview duck using the first duck from the cruise or a sample duck
const previewDuck = computed(() => {
  if (!props.cruise || !props.cruise.ducks) {
    console.log('No cruise or ducks available for previewDuck, using fallback');
    return createFallbackDuck();
  }
  
  const firstDuck = Array.isArray(props.cruise.ducks) && props.cruise.ducks.length > 0 
    ? props.cruise.ducks[0] 
    : null;
    
  if (firstDuck) {
    console.log('Using first cruise duck for preview:', firstDuck);
    return {
      ...firstDuck,
      uuid: firstDuck.uuid || 'preview-duck-uuid',
      id: typeof firstDuck.id === 'string' ? parseInt(firstDuck.id, 10) : firstDuck.id,
      found: 1, // Always show as found in preview
      idCruise: props.cruise.id,
      image: firstDuck.image || undefined,
      rarity: (firstDuck as any).rarity || 'COMMON'
    };
  }
  
  console.log('No ducks in cruise for previewDuck, using fallback');
  return createFallbackDuck();
});

// Create a fallback duck for preview
const createFallbackDuck = () => {
  return {
    uuid: 'preview-duck-uuid',
    id: 1,
    name: 'Sample Duck',
    found: 1,
    image: undefined, // Use default duck image from SingleDuck component
    idCruise: props.cruise?.id || 0,
    rarity: 'RARE'
  };
};

// Setup preview data in stores when dialog opens
const setupPreviewData = () => {
  console.log('Setting up preview data with cruise:', props.cruise);
  
  // Store original data
  originalDucks = [...duckStore.ducks];
  originalCruise = cruiseStore.cruise ? { ...cruiseStore.cruise } : null;

  // Set preview cruise data
  cruiseStore.assignCurrentCruise({
    id: props.cruise.id,
    name: props.cruise.name,
    startDate: props.cruise.startDate,
    endDate: props.cruise.endDate,
    aboutUs: props.cruise.aboutUs,
    socialLink: props.cruise.socialLink,
    emailRecipients: props.cruise.emailRecipients || '',
    duck: [] // Provide empty array to satisfy the type
  });

  // Clear existing ducks first
  duckStore.ducks.length = 0;
  
  // Check if cruise has ducks before trying to iterate
  if (props.cruise.ducks && Array.isArray(props.cruise.ducks) && props.cruise.ducks.length > 0) {
    console.log(`Processing ${props.cruise.ducks.length} ducks for preview`);
    
    // Add each duck to the store
    props.cruise.ducks.forEach((duck, index) => {
      if (duck) {
        const duckData = {
          uuid: duck.uuid || `preview-duck-${index}`,
          id: typeof duck.id === 'string' ? parseInt(duck.id, 10) : duck.id,
          name: duck.name || `Duck ${index + 1}`,
          found: duck.found || 0,
          image: duck.image,
          idCruise: props.cruise.id,
          rarity: (duck as any).rarity || 'COMMON'
        };
        
        console.log(`Adding duck to store:`, duckData);
        duckStore.ducks.push(duckData);
      }
    });
  } else {
    console.log('No ducks available in cruise, adding a sample duck');
    // Add a sample duck if no ducks are available
    duckStore.ducks.push({
      uuid: 'preview-duck-uuid',
      id: 1,
      found: 1,
      image: undefined,
      idCruise: props.cruise.id,
      rarity: 'LEGENDARY'
    });
  }
  
  // Set the first duck as current duck if available
  if (duckStore.ducks.length > 0) {
    duckStore.currentDuck = duckStore.ducks[0];
    console.log('Current duck set to:', duckStore.currentDuck);
  } else {
    console.warn('No ducks available to set as current duck');
  }
};

// Restore original data when dialog closes
const restoreOriginalData = () => {
  console.log('Restoring original data to stores');
  
  try {
    // Restore original ducks
    duckStore.ducks.length = 0;
    
    if (Array.isArray(originalDucks) && originalDucks.length > 0) {
      console.log(`Restoring ${originalDucks.length} original ducks`);
      originalDucks.forEach(duck => {
        if (duck) {
          duckStore.ducks.push({...duck});
        }
      });
    } else {
      console.log('No original ducks to restore');
    }
    
    // Restore original cruise
    if (originalCruise) {
      console.log('Restoring original cruise:', originalCruise);
      // Make sure all required properties are present
      const cruiseToRestore = {
        ...originalCruise,
        duck: originalCruise.duck || [],
        emailRecipients: originalCruise.emailRecipients || ''
      };
      cruiseStore.assignCurrentCruise(cruiseToRestore);
    } else {
      console.log('No original cruise to restore');
    }
    
    // Restore original current duck if it existed
    if (duckStore.currentDuck) {
      if (originalDucks.length > 0) {
        const originalCurrentDuck = originalDucks.find(duck => 
          duck && (duck.uuid === duckStore.currentDuck?.uuid || 
          duck.id === duckStore.currentDuck?.id)
        );
        
        if (originalCurrentDuck) {
          console.log('Restoring original current duck:', originalCurrentDuck);
          duckStore.currentDuck = {...originalCurrentDuck};
        } else {
          console.log('Original current duck not found, setting first duck');
          duckStore.currentDuck = originalDucks[0] ? {...originalDucks[0]} : undefined;
        }
      } else {
        console.log('Clearing current duck as there were no original ducks');
        duckStore.currentDuck = undefined;
      }
    }
    
    console.log('Data restoration complete');
  } catch (error) {
    console.error('Error restoring original data:', error);
  }
};

// Watch for dialog visibility changes
watch(isVisible, (newValue, oldValue) => {
  if (newValue === true) {
    // Dialog is opening
    console.log('DuckViewPreview dialog opening, setting up data');
    setupPreviewData();
  } else if (newValue === false && oldValue === true) {
    // Dialog is closing
    console.log('DuckViewPreview dialog closing, restoring data');
    restoreOriginalData();
  }
}, { immediate: true });

// Also watch for changes to the cruise prop
watch(() => props.cruise, (newCruise) => {
  if (isVisible.value && newCruise) {
    console.log('Cruise data changed while preview is open, updating preview');
    setupPreviewData();
  }
}, { deep: true });

// Provide duck and cruise to child components (same as DuckView)
provide('duck', previewDuck);
provide('cruise', computed(() => props.cruise));

const closePreview = () => {
  isVisible.value = false;
};

// Setup on component mount if dialog is initially open
onMounted(() => {
  if (isVisible.value) {
    console.log('DuckViewPreview mounted with dialog open, setting up data');
    setupPreviewData();
  }
});

// Cleanup on component unmount
onUnmounted(() => {
  if (isVisible.value) {
    console.log('DuckViewPreview unmounting while open, restoring data');
    restoreOriginalData();
  }
});
</script>

<style scoped>
/* Enable scrolling on the v-card */
:deep(.v-card) {
  overflow-y: auto !important;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 1000;
}

/* Custom animations to match DuckView */
@keyframes wiggle {
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-3deg); }
  75% { transform: rotate(3deg); }
}

@keyframes fade-in {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes pulse-slow {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.animate-wiggle {
  animation: wiggle 0.5s ease-in-out;
}

.animate-fade-in {
  animation: fade-in 0.6s ease-out;
}

.animate-pulse-slow {
  animation: pulse-slow 2s infinite;
}

/* Scrollbar styling for webkit browsers */
:deep(.v-card)::-webkit-scrollbar {
  width: 8px;
}

:deep(.v-card)::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

:deep(.v-card)::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

:deep(.v-card)::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Firefox scrollbar */
:deep(.v-card) {
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
}

/* Media queries for better mobile responsiveness */
@media (max-width: 600px) {
  /* Increase tap target sizes on mobile */
  :deep(.v-btn) {
    min-height: 40px;
  }
}

/* Safe area insets for newer mobile devices */
@supports (padding: max(0px)) {
  main {
    padding-left: env(safe-area-inset-left, 0);
    padding-right: env(safe-area-inset-right, 0);
    padding-bottom: env(safe-area-inset-bottom, 0);
  }
}

/* Improve touch targets for all interactive elements */
:deep(button),
:deep(a),
:deep(.v-btn) {
  min-height: 44px;
  min-width: 44px;
  touch-action: manipulation;
}
</style>
